<template>
  <div class="flex h-screen max-h-screen bg-white">
    <!-- Sidebar -->
    <CorpSidebar v-if="showSidebar" />

    <!-- Main Content -->
    <main
      id="main-view"
      class="w-full h-full flex-1 flex flex-col overflow-hidden"
    >
      <CorpToolbar v-if="showToolbar" class="flex-shrink-0" />

      <div
        class="flex flex-1 min-h-0 flex-col overflow-y-auto"
        :class="mainContentScrollClass"
      >
        <RouterView
          v-slot="{ Component }"
          :key="$route.path"
        >
          <component
            :is="Component"
            class="flex w-full min-h-0 flex-1 flex-col"
          />
        </RouterView>
      </div>
    </main>
  </div>

  <!-- Small Screen Banner -->
  <EcxSmallScreenBanner
    v-if="showSmallScreenBanner"
    :title="t('corp.small_screen.title')"
    :message="t('corp.small_screen.message')"
    :exit-button-text="t('corp.small_screen.exit_button')"
    logo-src="/assets/images/ecx_logo.webp"
  />

  <!-- Toast Container -->
  <EcxFeedbackToastContainer />
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterView, useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useI18n } from 'vue-i18n'

import CorpSidebar from '@/components/navigation/CorpSidebar.vue'
import CorpToolbar from '@/components/navigation/CorpToolbar.vue'

import { EcxSmallScreenBanner, EcxFeedbackToastContainer } from '@ecx/ui'

import { useCompanyStore } from '@/stores/company'

const { t } = useI18n()
const route = useRoute()
const companyStore = useCompanyStore()

const mainContentScrollClass = computed(() =>
  route.meta.scrollMainContent === false ? 'overflow-hidden' : 'overflow-y-auto',
)

// Computed para controlar quando mostrar sidebar/toolbar
const showSidebar = computed(() => route.meta.showSidebar !== false)
const showToolbar = computed(() => route.meta.showToolbar !== false)
/** No documento do iframe a viewport é estreita; `desktop:hidden` do banner dispararia em falso. */
function isAppEmbeddedInIframe(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/** Webviews / iframes estreitos (token SWAP, Flutter); nunca bloquear com banner de “tela pequena”. */
function isSwapVirtualCardViewerRoute(routeRef: RouteLocationNormalizedLoaded): boolean {
  const routeName = routeRef.name
  if (routeName === 'swap-virtual-card' || routeName === 'swap-virtual-card-mobile') {
    return true
  }
  if (routeRef.path === '/swap-virtual-card') {
    return true
  }
  return routeRef.path.startsWith('/app/dados-cartao')
}

const showSmallScreenBanner = computed(() => {
  if (isAppEmbeddedInIframe()) {
    return false
  }
  if (isSwapVirtualCardViewerRoute(route)) {
    return false
  }
  return route.meta.showSmallScreenBanner !== false
})

// /manager/me é carregado no router guard (uma vez); aqui só dados adicionais (ex.: empresas)
onMounted(async () => {
  if (route.meta.requiresAuth === false) return
  await companyStore.fetchCompanies()
})
</script>

<style>
@import '@/styles/index.css';
</style>
